import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { delegationService } from '../services/delegationService';
import { VotingDelegation } from '../types/delegation';
import { notifyError } from '../utils/notificationCenter';
import { XCircle } from 'lucide-react';
import LoadingState from '../components/common/LoadingState';

const RejectDelegationPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [delegation, setDelegation] = useState<VotingDelegation | null>(null);
  
  // Guard para prevenir doble ejecución en React StrictMode
  const hasExecuted = useRef(false);

  const handleReject = async () => {
    if (!id || !token) {
      const errorMsg = 'Enlace inválido';
      notifyError(errorMsg);
      setError(errorMsg);
      return;
    }

    setLoading(true);

    try {
      const result = await delegationService.rejectDelegation(id, token);
      setDelegation(result);
      setSuccess(true);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Error al rechazar la delegación';
      notifyError(errorMsg);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Prevenir doble ejecución (React StrictMode ejecuta effects dos veces en desarrollo)
    if (hasExecuted.current) return;
    
    if (id && token) {
      hasExecuted.current = true;
      // Auto-ejecutar el rechazo
      handleReject();
    } else {
      const errorMsg = 'Enlace inválido o expirado';
      notifyError(errorMsg);
      setError(errorMsg);
    }
  }, [id, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <LoadingState layout="inline" size="lg" message="Procesando rechazo..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Error al Rechazar
            </h2>
            <p className="text-red-600 mb-6">
              {error}
            </p>
            <p className="text-sm text-gray-500">
              Verifica que el enlace sea correcto.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (success && delegation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 mb-4">
              <svg className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              <span className="inline-flex items-center justify-center gap-2">
                <XCircle className="h-6 w-6 text-orange-600" aria-hidden="true" />
                <span>Delegación Rechazada</span>
              </span>
            </h2>
            <p className="text-gray-600 mb-6">
              Has rechazado la solicitud de delegación de voto.
            </p>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-semibold text-gray-900 mb-2">
                ¿Qué sucede ahora?
              </h3>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>✓ El delegador fue notificado de tu decisión</li>
                <li>✓ El delegador podrá votar normalmente</li>
                <li>✓ No tienes ninguna obligación adicional</li>
              </ul>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                Gracias por responder la solicitud de delegación.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default RejectDelegationPage;
